import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import Tooltip from '@components/Tooltip';
import Alert from '@components/Toaster/Alert';
import TextEditor from '@components/TextEditor';
import MediaUploaderMulti from '@components/MediaUploaderMulti';
import Button from '@components/Button';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { GoogleIcon, FacebookIcon, InstagramIcon, tiktokIcon, XIcon, LinkedInIcon } from 'assets/images/integrations';
import StoryRingIcon from '@components/StoryRingIcon';
import { socialPostsService } from '@services';
import { POST_CHANNEL, POST_CHANNEL_LABEL, isStoryPlatform } from '@constants/social-posts';
import { PLACEHOLDER_CONTEXT, getPlaceholderTokensForContext } from '@constants/placeholders';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { KB, MB } from '@constants/file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { formatVideoDuration, formatVideoSize } from '@helpers/upload-file';
import { useTranslation } from '@hooks';
import {
    PLATFORM_REQUIREMENTS,
    BASE_MEDIA_LIMIT,
    getBaseMessageLimit,
    getDisplayedMessageLimit,
    getBaseMessageLengthCounter,
    getPlatformLengthCounter,
    getPlatformIssues,
    getVideoSpec,
    getVideoViolations,
    getVideoUploadValidation,
    getVideoConstraint,
    isVideoUnsupported,
    captionMediaCount,
    resolveOverrideMessage,
    resolveOverrideMedia,
    seedOverride,
    seedOverrideMessage,
    seedOverrideMedia,
    isOverrideMessageMissing,
    canResetOverrideMessage,
    canResetOverrideMedia,
} from '../platformRules';
import { useStoryAspectWarning } from '../useStoryAspectWarning';
import { TIKTOK_ISSUE } from '../tiktokRules';
import TikTokSettings, { TikTokTabNotices } from './TikTokSettings';
import {
    SectionHeading,
    CssTabs,
    CssTab,
    TabLabel,
    Panel,
    FieldHeading,
    CustomizeRow,
    CtaRow,
    Notices,
    StatusDot,
    CustomizedDot,
} from './styles';

const POST_PLACEHOLDER_TOKENS = getPlaceholderTokensForContext(PLACEHOLDER_CONTEXT.POST);

const ALL_PLATFORMS_TAB = 'all';

// Loading is conveyed by the disabled controls, and a failed fetch gets its own alert with a retry
// link, so neither belongs in the generic warning list or on the tab's error dot.
const TIKTOK_ISSUES_HIDDEN_FROM_NOTICES = [TIKTOK_ISSUE.CREATOR_INFO_LOADING, TIKTOK_ISSUE.CREATOR_INFO_FAILED];

const PLATFORM_META = {
    [POST_CHANNEL.GOOGLE]: { icon: <GoogleIcon width={18} height={18} />, mediaRulesKey: 'google' },
    [POST_CHANNEL.FACEBOOK]: { icon: <FacebookIcon width={18} height={18} />, mediaRulesKey: 'facebook' },
    [POST_CHANNEL.INSTAGRAM]: { icon: <InstagramIcon width={18} height={18} />, mediaRulesKey: 'instagram' },
    [POST_CHANNEL.TIKTOK]: {
        icon: <img src={tiktokIcon} alt="" width={18} height={18} style={{ display: 'inline-block' }} />,
        mediaRulesKey: 'tiktok',
    },
    [POST_CHANNEL.X]: { icon: <XIcon width={18} height={18} />, mediaRulesKey: 'x' },
    [POST_CHANNEL.LINKEDIN]: { icon: <LinkedInIcon width={18} height={18} />, mediaRulesKey: 'linkedin' },
    [POST_CHANNEL.FACEBOOK_STORY]: {
        icon: (
            <StoryRingIcon size={18}>
                <FacebookIcon />
            </StoryRingIcon>
        ),
        mediaRulesKey: 'facebookStory',
    },
    [POST_CHANNEL.INSTAGRAM_STORY]: {
        icon: (
            <StoryRingIcon size={18}>
                <InstagramIcon />
            </StoryRingIcon>
        ),
        mediaRulesKey: 'instagramStory',
    },
};

// dimension mirrors backend SocialMediaLimits.MinImageDimension — keep the two in step.
const IMAGE_VALIDATION = { maxSize: 10 * MB, minSize: 10 * KB, dimension: [250, 250] };

const uploadImage = data => socialPostsService.uploadSocialPostImages(data);
const uploadVideo = (data, onProgress) => socialPostsService.uploadSocialPostVideos(data, onProgress);

const platformLabel = platform => POST_CHANNEL_LABEL[platform] || `Platform ${platform}`;

const PlatformNotices = ({ issues, t }) => {
    if (!issues.length) return null;
    return (
        <Notices>
            {issues.map((issue, index) => (
                <Alert key={`${issue.code}:${index}`} severity="warning" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {t(issue.key, issue.options)}
                    </Typography>
                </Alert>
            ))}
        </Notices>
    );
};

const StoryAspectNotice = ({ media, platform, t }) => {
    const offRatio = useStoryAspectWarning(media);
    if (!offRatio) return null;
    return (
        <Alert severity="warning" sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
                {t('SocialPosts.storyAspectWarning', { platform: platformLabel(platform) })}
            </Typography>
        </Alert>
    );
};

const FieldTitle = ({ title, description, error = false, onReset, t }) => {
    const titleId = useId();
    const titleRef = useRef(null);
    const reset = () => {
        onReset();
        titleRef.current?.focus();
    };
    return (
        <FieldHeading>
            <SectionHeading>
                <Typography
                    id={titleId}
                    ref={titleRef}
                    tabIndex={-1}
                    variant="body2"
                    color={error ? 'error' : 'textPrimary'}
                >
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {description}
                    </Typography>
                )}
            </SectionHeading>
            {onReset && (
                <Button variant="text" startIcon={<UndoRoundedIcon />} onClick={reset} aria-describedby={titleId}>
                    {t('buttons.reset')}
                </Button>
            )}
        </FieldHeading>
    );
};

const ContentTabs = ({
    disabled,
    platforms = [],
    message,
    media,
    messageError,
    mediaError,
    onMessageChange,
    onMediaChange,
    button,
    buttonContent,
    buttonOptions = [],
    buttonContentError,
    onButtonChange,
    onButtonContentChange,
    overrides = [],
    onOverridesChange,
    isUploading = false,
    setIsUploading,
    selectedCompanies = [],
    isMeasuringVideos = false,
    tiktok = null,
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(ALL_PLATFORMS_TAB);

    const overrideByPlatform = useMemo(() => {
        const map = {};
        (overrides || []).forEach(o => {
            map[o.platform] = o;
        });
        return map;
    }, [overrides]);

    useEffect(() => {
        if (activeTab !== ALL_PLATFORMS_TAB && !platforms.includes(activeTab)) {
            setActiveTab(ALL_PLATFORMS_TAB);
        }
    }, [platforms, activeTab]);

    const issuesByPlatform = useMemo(() => {
        const map = {};
        platforms.forEach(platform => {
            const override = overrideByPlatform[platform];
            map[platform] = getPlatformIssues(
                platform,
                resolveOverrideMessage(override, message),
                resolveOverrideMedia(override, media),
                POST_PLACEHOLDER_TOKENS,
            );
        });
        if (tiktok && map[POST_CHANNEL.TIKTOK]) {
            map[POST_CHANNEL.TIKTOK] = [
                ...map[POST_CHANNEL.TIKTOK],
                ...tiktok.issues.filter(i => !TIKTOK_ISSUES_HIDDEN_FROM_NOTICES.includes(i.code)),
            ];
        }
        return map;
    }, [platforms, message, media, overrides, tiktok?.issues]);

    const baseMessageLimit = useMemo(() => getBaseMessageLimit(platforms), [platforms]);
    const baseCounterLength = useMemo(() => getBaseMessageLengthCounter(platforms), [platforms]);

    const baseInheritingPlatforms = useMemo(
        () => platforms.filter(p => overrideByPlatform[p]?.media == null),
        [platforms, overrideByPlatform],
    );
    const baseVideoValidation = useCallback(
        count => getVideoUploadValidation(baseInheritingPlatforms, count),
        [baseInheritingPlatforms],
    );
    const baseVideoConstraint = useMemo(
        () => getVideoConstraint(baseInheritingPlatforms, captionMediaCount(media || [])),
        [baseInheritingPlatforms, media],
    );
    const baseVideoUnsupported = useMemo(() => isVideoUnsupported(baseInheritingPlatforms), [baseInheritingPlatforms]);
    const baseVideoConstrainers = useMemo(
        () =>
            [...new Set([baseVideoConstraint.sizeLimitedBy, baseVideoConstraint.durationLimitedBy])]
                .filter(p => p != null)
                .map(platformLabel)
                .join(', '),
        [baseVideoConstraint],
    );

    const videoViolationSummary = useMemo(() => {
        const tooLarge = [];
        const tooLong = [];
        const tooSmall = [];
        const tooWide = [];
        baseInheritingPlatforms.forEach(platform => {
            const violations = getVideoViolations(platform, media || []);
            if (violations.tooLarge) {
                tooLarge.push(`${platformLabel(platform)} (${formatVideoSize(violations.spec.maxBytes)})`);
            }
            if (violations.tooLong) {
                tooLong.push(`${platformLabel(platform)} (${formatVideoDuration(violations.spec.maxDurationSeconds)})`);
            }
            if (violations.tooSmall) {
                tooSmall.push(`${platformLabel(platform)} (${formatVideoSize(violations.spec.minBytes)})`);
            }
            if (violations.tooWide) {
                tooWide.push(`${platformLabel(platform)} (${violations.spec.maxWidth}×${violations.spec.maxHeight})`);
            }
        });
        return { tooLarge, tooLong, tooSmall, tooWide };
    }, [baseInheritingPlatforms, media]);

    const selectedLocationIds = selectedCompanies.map(c => c.id);

    const aiAssistant = {
        additionalOptions: [
            {
                text: t('AIAssistant.writePost'),
                type: AITaskType.GenerateSocialPost,
                taskType: AI_TASK_TYPE.CONTEXTUAL,
                companyId: selectedLocationIds[0],
                locationIds: selectedLocationIds,
                maxCharacterLimit: baseMessageLimit,
            },
        ],
    };

    const aiAssistantFor = platform => ({
        additionalOptions: [
            {
                text: t('AIAssistant.optimizeForPlatform', { platform: platformLabel(platform) }),
                type: AITaskType.GenerateSocialPost,
                taskType: AI_TASK_TYPE.CONTEXTUAL,
                companyId: selectedLocationIds[0],
                locationIds: selectedLocationIds,
                platform,
            },
        ],
    });

    const upsertOverride = (platform, patch) => {
        const rest = (overrides || []).filter(o => o.platform !== platform);
        const current = overrideByPlatform[platform] ?? { platform };
        onOverridesChange([...rest, { ...current, ...patch }]);
    };

    const resetOverrideMessage = platform =>
        upsertOverride(platform, { message: seedOverrideMessage(platform, message) });

    const resetOverrideMedia = platform => upsertOverride(platform, { media: seedOverrideMedia(platform, media) });

    const toggleCustomize = (platform, enabled) => {
        if (enabled) {
            if (overrideByPlatform[platform]) return;
            onOverridesChange([...(overrides || []), seedOverride(platform, message, media)]);
        } else {
            onOverridesChange((overrides || []).filter(o => o.platform !== platform));
        }
    };

    const renderAllPlatformsPanel = () => (
        <Panel>
            <FieldTitle title={t('common.message', { count: 1 })} error={!!messageError} t={t} />
            <TextEditor
                initialValue={message || ''}
                onChange={text => onMessageChange(parseTipTapJSONToPlainText(text))}
                limit={getDisplayedMessageLimit(baseMessageLimit)}
                getLength={baseCounterLength}
                editable={!disabled}
                error={messageError}
                helperText={messageError ? t(messageError.message, { count: baseMessageLimit }) : null}
                placeholderContext={PLACEHOLDER_CONTEXT.POST}
                aiAssistant={aiAssistant}
                toolbar={<PostDynamicVariables disabled={disabled} />}
            />
            <FieldTitle
                title={t('SocialPosts.media')}
                description={
                    <>
                        {t('SocialPosts.imageFormat')}
                        {baseVideoUnsupported ? null : (
                            <>
                                <br />
                                {t('SocialPosts.videoFormatDynamic', {
                                    maxSize: formatVideoSize(baseVideoConstraint.maxBytes),
                                    maxResolution: `${baseVideoConstraint.maxWidth}×${baseVideoConstraint.maxHeight}`,
                                    minDuration: formatVideoDuration(baseVideoConstraint.minDurationSeconds),
                                    maxDuration: formatVideoDuration(baseVideoConstraint.maxDurationSeconds),
                                })}
                                {baseVideoConstrainers
                                    ? ` ${t('SocialPosts.videoConstrainedBy', { platform: baseVideoConstrainers })}`
                                    : ''}
                            </>
                        )}
                    </>
                }
                t={t}
            />
            <MediaUploaderMulti
                limit={BASE_MEDIA_LIMIT}
                imagesValidation={IMAGE_VALIDATION}
                videoValidation={baseVideoValidation}
                extensions={baseVideoUnsupported ? '.png, .jpg, .jpeg' : '.png, .jpg, .jpeg, .mp4'}
                disabled={disabled}
                data={media || []}
                syncData
                onChange={onMediaChange}
                imageServiceCallback={uploadImage}
                videoServiceCallback={uploadVideo}
                setIsUploading={setIsUploading}
            />
            {isMeasuringVideos && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
                    {t('SocialPosts.checkingVideoLength')}
                </Typography>
            )}
            {videoViolationSummary.tooLarge.length > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)', display: 'block' }}>
                    {t('SocialPosts.videoTooLargeSummary', { platforms: videoViolationSummary.tooLarge.join(', ') })}
                </Typography>
            )}
            {videoViolationSummary.tooLong.length > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)', display: 'block' }}>
                    {t('SocialPosts.videoTooLongSummary', { platforms: videoViolationSummary.tooLong.join(', ') })}
                </Typography>
            )}
            {videoViolationSummary.tooSmall.length > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)', display: 'block' }}>
                    {t('SocialPosts.videoTooSmallSummary', { platforms: videoViolationSummary.tooSmall.join(', ') })}
                </Typography>
            )}
            {videoViolationSummary.tooWide.length > 0 && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)', display: 'block' }}>
                    {t('SocialPosts.videoTooWideSummary', { platforms: videoViolationSummary.tooWide.join(', ') })}
                </Typography>
            )}
            {mediaError && (
                <Typography variant="caption" sx={{ color: 'var(--error-main)', display: 'block' }}>
                    {t(mediaError.message)}
                </Typography>
            )}
        </Panel>
    );

    const videoRuleLine = (platform, items) => {
        if (!getVideoSpec(platform)) return '';
        const constraint = getVideoConstraint([platform], captionMediaCount(items));
        return ` ${t('SocialPosts.videoFormatDynamic', {
            maxSize: formatVideoSize(constraint.maxBytes),
            maxResolution: `${constraint.maxWidth}×${constraint.maxHeight}`,
            minDuration: formatVideoDuration(constraint.minDurationSeconds),
            maxDuration: formatVideoDuration(constraint.maxDurationSeconds),
        })}`;
    };

    const renderPlatformMediaTitle = (platform, override, effectiveMedia) => (
        <FieldTitle
            title={t('SocialPosts.media')}
            description={`${t(`SocialPosts.mediaRules.${PLATFORM_META[platform].mediaRulesKey}`)}${videoRuleLine(platform, effectiveMedia)}`}
            onReset={
                override && !disabled && !isUploading && canResetOverrideMedia(override, media)
                    ? () => resetOverrideMedia(platform)
                    : undefined
            }
            t={t}
        />
    );

    const renderStoryPanel = platform => {
        const req = PLATFORM_REQUIREMENTS[platform];
        const override = overrideByPlatform[platform];
        const isCustom = override !== undefined;
        const issues = issuesByPlatform[platform] || [];
        const effectiveMedia = resolveOverrideMedia(override, media);
        const videoValidation = count => getVideoUploadValidation([platform], count);

        return (
            <Panel>
                <PlatformNotices issues={issues} t={t} />
                <Alert severity="warning" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {t('SocialPosts.storyMediaOnlyNotice')}
                    </Typography>
                </Alert>
                <CustomizeRow>
                    <Switch
                        checked={isCustom}
                        handleChange={e => toggleCustomize(platform, e.target.checked)}
                        label={t('SocialPosts.customizeFor', { platform: platformLabel(platform) })}
                        disabled={disabled}
                    />
                </CustomizeRow>

                {renderPlatformMediaTitle(platform, override, effectiveMedia)}

                {isCustom ? (
                    <MediaUploaderMulti
                        key={`media-${platform}-edit`}
                        limit={req.maxMedia}
                        imagesValidation={IMAGE_VALIDATION}
                        videoValidation={videoValidation}
                        extensions={req.extensions}
                        disabled={disabled}
                        data={effectiveMedia}
                        syncData
                        onChange={updatedMedia => upsertOverride(platform, { media: updatedMedia })}
                        imageServiceCallback={uploadImage}
                        videoServiceCallback={uploadVideo}
                        setIsUploading={setIsUploading}
                    />
                ) : (
                    <MediaUploaderMulti
                        key={`media-${platform}-view`}
                        limit={BASE_MEDIA_LIMIT}
                        extensions={req.extensions}
                        disabled
                        data={media || []}
                        onChange={() => {}}
                    />
                )}

                <StoryAspectNotice media={effectiveMedia} platform={platform} t={t} />
            </Panel>
        );
    };

    const renderPlatformPanel = platform => {
        if (isStoryPlatform(platform)) return renderStoryPanel(platform);

        const req = PLATFORM_REQUIREMENTS[platform];
        const override = overrideByPlatform[platform];
        const isCustom = override !== undefined;
        const issues = issuesByPlatform[platform] || [];
        const counterLength = getPlatformLengthCounter(platform);
        const effectiveMedia = resolveOverrideMedia(override, media);
        const videoValidation = count => getVideoUploadValidation([platform], count);
        const isTikTok = platform === POST_CHANNEL.TIKTOK && tiktok;
        const displayedMessageLimit = getDisplayedMessageLimit(req.maxMessageLength);
        const canResetMessage = isCustom && !disabled && canResetOverrideMessage(override, message);
        const messageMissing = !disabled && isOverrideMessageMissing(override);

        return (
            <Panel>
                <PlatformNotices issues={issues} t={t} />
                {isTikTok && !disabled && <TikTokTabNotices creatorInfo={tiktok.creatorInfo} />}
                <CustomizeRow>
                    <Switch
                        checked={isCustom}
                        handleChange={e => toggleCustomize(platform, e.target.checked)}
                        label={t('SocialPosts.customizeFor', { platform: platformLabel(platform) })}
                        disabled={disabled}
                    />
                </CustomizeRow>

                <FieldTitle
                    title={t('common.message', { count: 1 })}
                    error={messageMissing}
                    onReset={canResetMessage ? () => resetOverrideMessage(platform) : undefined}
                    t={t}
                />
                {isCustom ? (
                    <TextEditor
                        key={`msg-${platform}-edit`}
                        initialValue={override.message || ''}
                        onChange={text => upsertOverride(platform, { message: parseTipTapJSONToPlainText(text) })}
                        limit={displayedMessageLimit}
                        editable={!disabled}
                        error={messageMissing}
                        helperText={messageMissing ? t('validation.required') : null}
                        placeholderContext={PLACEHOLDER_CONTEXT.POST}
                        aiAssistant={aiAssistantFor(platform)}
                        getLength={counterLength}
                        toolbar={<PostDynamicVariables disabled={disabled} />}
                    />
                ) : (
                    <TextEditor
                        key={`msg-${platform}-view`}
                        initialValue={message || ''}
                        onChange={() => {}}
                        limit={displayedMessageLimit}
                        editable={false}
                        placeholderContext={PLACEHOLDER_CONTEXT.POST}
                        getLength={counterLength}
                    />
                )}

                {renderPlatformMediaTitle(platform, override, effectiveMedia)}

                {isCustom ? (
                    <MediaUploaderMulti
                        key={`media-${platform}-edit`}
                        limit={req.maxMedia}
                        imagesValidation={IMAGE_VALIDATION}
                        videoValidation={videoValidation}
                        extensions={req.extensions}
                        disabled={disabled}
                        // Show inherited base media for a null override (matches preview/publish);
                        // editing it writes an explicit array, making the platform custom.
                        data={effectiveMedia}
                        syncData
                        onChange={updatedMedia => upsertOverride(platform, { media: updatedMedia })}
                        imageServiceCallback={uploadImage}
                        videoServiceCallback={uploadVideo}
                        setIsUploading={setIsUploading}
                    />
                ) : (
                    // Read-only preview of the base media this platform inherits (resolved as-is,
                    // not trimmed); keyed per platform so it re-reads the current base on switch.
                    <MediaUploaderMulti
                        key={`media-${platform}-view`}
                        limit={BASE_MEDIA_LIMIT}
                        extensions={req.extensions}
                        disabled
                        data={media || []}
                        onChange={() => {}}
                    />
                )}

                {isTikTok && <TikTokSettings disabled={disabled} tiktok={tiktok} media={effectiveMedia} />}

                {platform === POST_CHANNEL.GOOGLE && (
                    <CtaRow>
                        <Select
                            value={button}
                            onChange={({ target: { value } }) => onButtonChange(value)}
                            label={t('common.button', { count: 1 })}
                            fullWidth
                            options={buttonOptions}
                            disabled={disabled}
                        />
                        {button !== 0 && button !== 6 && (
                            <Input
                                inputProps={{
                                    value: buttonContent || '',
                                    onChange: e => onButtonContentChange(e.target.value),
                                }}
                                label={t('common.link')}
                                fullWidth
                                error={!!buttonContentError}
                                helperText={buttonContentError ? t(buttonContentError.message) : null}
                                disabled={disabled}
                            />
                        )}
                    </CtaRow>
                )}
            </Panel>
        );
    };

    return (
        <div>
            <SectionHeading>
                <Typography variant="subtitle1" color="textPrimary">
                    {t('SocialPosts.content')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {t('SocialPosts.contentDescription')}
                </Typography>
            </SectionHeading>
            <CssTabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons={false}
            >
                <CssTab value={ALL_PLATFORMS_TAB} label={t('SocialPosts.allPlatforms')} />
                {platforms.map(platform => {
                    const issues = (issuesByPlatform[platform] || []).map(i => t(i.key, i.options));
                    if (!disabled && isOverrideMessageMissing(overrideByPlatform[platform])) {
                        issues.push(t('SocialPosts.messageRequired'));
                    }
                    const issuesSummary = issues.join(' ');
                    return (
                        <CssTab
                            key={platform}
                            value={platform}
                            label={
                                <TabLabel>
                                    {PLATFORM_META[platform].icon}
                                    {platformLabel(platform)}
                                    {overrideByPlatform[platform] && (
                                        <Tooltip placement="top" title={t('SocialPosts.customized')}>
                                            <CustomizedDot role="img" aria-label={t('SocialPosts.customized')} />
                                        </Tooltip>
                                    )}
                                    {issues.length > 0 && (
                                        <Tooltip placement="top" title={issuesSummary}>
                                            <StatusDot role="img" aria-label={issuesSummary} />
                                        </Tooltip>
                                    )}
                                </TabLabel>
                            }
                        />
                    );
                })}
            </CssTabs>
            {activeTab === ALL_PLATFORMS_TAB ? renderAllPlatformsPanel() : renderPlatformPanel(activeTab)}
        </div>
    );
};

export default ContentTabs;
